import { useCallback, useEffect, useRef, useState } from "react";
import { formatPhone } from "@/lib/utils";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { ADMIN_ROLES, canActOnListingOrg } from "@/lib/roles";
import { useToast } from "@/components/ui/toast";
import { api, assetUrl, ApiError } from "@/lib/api";
import { isFreeBuyerUser } from "@/lib/access";
import type { BusinessListing, User as UserType } from "@/types";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Globe, Calendar, CheckCircle, Pencil,
  Heart, Lock, TrendingUp,
  BarChart3, DollarSign, Building2, Mail, Phone,
  ChevronLeft, ChevronRight, MapPin, Download, MessageCircle, Calculator,
  Sparkles, AlertTriangle, LayoutGrid, LineChart, ClipboardList, Maximize2, X,
  Share2, Eye, Scale, Link2,
} from "lucide-react";
import ShareListingModal from "@/components/ShareListingModal";
import Loader from "@/components/ui/loader";
import { downloadListingPDF } from "@/components/ListingPDF";
import { InterestChat } from "@/components/InterestChat";
import AdvisorProfileModal from "@/components/AdvisorProfileModal";
import SbaCalculatorForm from "@/components/SbaCalculatorForm";
import FundingCta from "@/components/FundingCta";
import { useModuleVisibility } from "@/lib/moduleVisibility";
import ValuationCalculatorForm from "@/components/ValuationCalculatorForm";
import FinancialModelForm from "@/components/FinancialModelForm";
import { SBA_DEFAULTS, SBA_MIN_DSCR, computeSba, type SbaInputs, type SbaResult } from "@/lib/sbaMath";

// ── Brand colours ─────────────────────────────────────────────────────────────
// Warm-dark banner tones (was navy/blue; recolored to the dark/gold theme).
const NAVY  = "#26200f";
const NAVY2 = "#1a150b";
const GOLD  = "#E8A020";
const RED   = "#CC3333";
const GREEN = "#16A34A";
const TEAL  = "#0D9488";
const GREY  = "#64748B";
const ORANGE = "#EA580C";
// Theme-aware primary text. Flips near-black (light) → near-white (dark) via the
// palette variables in index.css, so value text stays readable in both themes.
// Use this for text sitting on card/page surfaces instead of the hardcoded darks
// above (those stay dark-on-dark and wash out under data-theme="dark").
const INK = "var(--color-foreground)";

interface LookupItem { id: number; name: string }

// Daily-outreach + monthly-unlock counters behind the advisor card meters.
// GET /tiers/outreach-usage. *_limit is null when the tier is unlimited.
interface OutreachUsage {
  has_tier: boolean;
  tier_name?: string | null;
  messages_limit: number | null;
  messages_used: number;
  messages_remaining: number | null;
  deals_limit: number | null;
  deals_used: number;
  deals_remaining: number | null;
}

// ── Buy-box fit rows (drives the "Deal-Fit vs Your Buy-Box" panel) ────────────
// Scores come from GET /business-listings/{id}/deal-score — the same BuyBox
// engine behind the AI Deal Scoring page and the recommended feed — so the
// panel, feed, and scoring page always agree on a listing's fit.
type BuyBoxStatus = "match" | "watch" | "miss";
type BuyBoxRow = { label: string; status: BuyBoxStatus; detail: string };

interface DealScoreMetric { key: string; label: string; value: number }
interface DealScoreResponse {
  has_preferences: boolean;
  deal: { fit_score: number; metrics: DealScoreMetric[] } | null;
}

function metricStatus(value: number): BuyBoxStatus {
  return value >= 85 ? "match" : value >= 55 ? "watch" : "miss";
}

// ── BL Score — weighted acquisition-intelligence score from listing data ──────
type BlCategory = { label: string; score: number; weight: number; note: string };
function computeBlScore(listing: BusinessListing): { score: number; categories: BlCategory[] } {
  const cats: BlCategory[] = [];

  // Financial disclosure quality (25)
  const hasRev = listing.revenue != null;
  const hasEb  = listing.ebitda != null;
  const margin = hasRev && hasEb && listing.revenue! > 0 ? listing.ebitda! / listing.revenue! : null;
  let fin = 0;
  let finNote = "No financials disclosed";
  if (hasRev && hasEb) {
    fin = 70 + (margin != null ? (margin >= 0.25 ? 30 : margin >= 0.15 ? 20 : margin > 0 ? 10 : 0) : 0);
    finNote = margin != null ? `Revenue & EBITDA disclosed · ${(margin * 100).toFixed(1)}% margin` : "Revenue & EBITDA disclosed";
  } else if (hasRev || hasEb) {
    fin = 40;
    finNote = "Partial financials disclosed";
  }
  cats.push({ label: "Financial Disclosure", score: fin, weight: 25, note: finNote });

  // Deal economics — price vs cash flow (20)
  let econ = 50;
  let econNote = "Cash-flow multiple unavailable";
  if (listing.asking_price && listing.ebitda && listing.ebitda > 0) {
    const mult = listing.asking_price / listing.ebitda;
    econ = mult < 3 ? 95 : mult <= 4 ? 75 : mult <= 5 ? 55 : 30;
    econNote = `${mult.toFixed(1)}× cash-flow multiple`;
  }
  cats.push({ label: "Deal Economics", score: econ, weight: 20, note: econNote });

  // Business maturity (15)
  let mat = 50;
  let matNote = "Established year not disclosed";
  if (listing.established_year) {
    const age = new Date().getFullYear() - listing.established_year;
    mat = age >= 10 ? 95 : age >= 5 ? 80 : age >= 2 ? 55 : 30;
    matNote = `${age} year${age === 1 ? "" : "s"} in business`;
  }
  cats.push({ label: "Business Maturity", score: mat, weight: 15, note: matNote });

  // Advisor vetting (15)
  const vet = listing.agent_id ? (listing.agent_verified ? 100 : 85) : 25;
  cats.push({
    label: "Advisor Vetting",
    score: vet,
    weight: 15,
    note: listing.agent_id ? (listing.agent_verified ? "Verified BL Advisor assigned" : "BL Advisor assigned") : "No BL Advisor assigned",
  });

  // Financing readiness (15)
  const finRdy = listing.is_sba_approved && listing.seller_financing ? 100 : listing.is_sba_approved ? 75 : listing.seller_financing ? 65 : 30;
  cats.push({
    label: "Financing Readiness",
    score: finRdy,
    weight: 15,
    note: listing.is_sba_approved && listing.seller_financing
      ? "SBA pre-approved + seller financing"
      : listing.is_sba_approved ? "SBA pre-approved" : listing.seller_financing ? "Seller financing offered" : "No financing signals",
  });

  // Listing completeness (10)
  let comp = 0;
  if (listing.overview)          comp += 40;
  if (listing.listing_image_url) comp += 30;
  if (listing.website_url)       comp += 30;
  cats.push({ label: "Listing Completeness", score: comp, weight: 10, note: `${comp}/100 profile completeness` });

  const totalWeight = cats.reduce((s, c) => s + c.weight, 0);
  const score = Math.round(cats.reduce((s, c) => s + c.score * c.weight, 0) / totalWeight);
  return { score, categories: cats };
}

// ── Estimated DSCR — the SBA calculator's own model (lib/sbaMath) at its
// default terms, fed with this listing's price + EBITDA ─────────────────────
function sbaEstimateInputs(askingPrice: number, ebitda: number): SbaInputs {
  return { ...SBA_DEFAULTS, askingPrice, cashFlowBasis: "ebitda", cashFlow: ebitda };
}
function estimateDscr(askingPrice: number, ebitda: number): (SbaResult & { dscr: number }) | null {
  if (askingPrice <= 0 || ebitda <= 0) return null;
  const r = computeSba(sbaEstimateInputs(askingPrice, ebitda));
  return r.dscr === null ? null : { ...r, dscr: r.dscr };
}

// ── Formatters ────────────────────────────────────────────────────────────────
const fmt = (v: number | null): string => {
  if (!v) return "—";
  if (v >= 1_000_000_000) return `$${+(v / 1_000_000_000).toFixed(v % 1_000_000_000 === 0 ? 0 : 1)}B`;
  if (v >= 1_000_000)     return `$${+(v / 1_000_000).toFixed(v % 1_000_000 === 0 ? 0 : 1)}M`;
  if (v >= 1_000)         return `$${+(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}K`;
  return `$${v}`;
};

const scoreColor = (s: number) => (s >= 75 ? GREEN : s >= 50 ? GOLD : RED);

// ── Small presentational pieces ───────────────────────────────────────────────
function ScoreDonut({ score, size = 132 }: { score: number; size?: number }) {
  const r = 52;
  const circ = 2 * Math.PI * r;
  const filled = (score / 100) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 132 132">
      <circle cx="66" cy="66" r={r} fill="none" stroke="#E2E8F0" strokeWidth="11" />
      <circle
        cx="66" cy="66" r={r} fill="none"
        stroke={scoreColor(score)} strokeWidth="11"
        strokeDasharray={`${filled} ${circ - filled}`}
        strokeDashoffset={circ / 4}
        strokeLinecap="round"
        style={{ transition: "stroke-dasharray 0.6s ease" }}
      />
      <text x="66" y="64" textAnchor="middle" style={{ fill: INK }} fontSize="30" fontWeight="800">{score}</text>
      <text x="66" y="84" textAnchor="middle" fill="#94A3B8" fontSize="11" fontWeight="600">/ 100</text>
    </svg>
  );
}

function ScoreBarRow({ label, score, note }: { label: string; score: number; note: string }) {
  return (
    <div className="group relative">
      <div className="flex items-center gap-3">
        <span className="w-40 shrink-0 text-xs font-semibold text-gray-600">{label}</span>
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
          <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: scoreColor(score), transition: "width 0.6s ease" }} />
        </div>
        <span className="w-8 shrink-0 text-right text-xs font-extrabold text-gray-800">{score}</span>
      </div>
      <p className="mt-0.5 pl-[10.75rem] text-[0.62rem] text-gray-400 hidden group-hover:block">{note}</p>
    </div>
  );
}

const BUYBOX_DOT: Record<BuyBoxStatus, string> = { match: GREEN, watch: GOLD, miss: RED };
function BuyBoxPanel({ pct, rows, dark = false }: { pct: number; rows: BuyBoxRow[]; dark?: boolean }) {
  const labelCls = dark ? "text-white/70" : "text-gray-600";
  const valueCls = dark ? "text-white" : "text-gray-900";
  return (
    <div>
      <p className={`text-[0.6rem] font-bold uppercase tracking-widest ${dark ? "text-white/50" : "text-gray-400"}`}>
        Deal-fit vs. your buy-box
      </p>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={`text-4xl font-extrabold ${valueCls}`}>{pct}%</span>
        <span className="text-xs font-bold" style={{ color: pct >= 70 ? "#4ADE80" : pct >= 40 ? "#FBBF24" : "#F87171" }}>
          {pct >= 70 ? "Strong match" : pct >= 40 ? "Partial match" : "Weak match"}
        </span>
      </div>
      <div className={`mt-2 h-1.5 w-full overflow-hidden rounded-full ${dark ? "bg-white/15" : "bg-gray-100"}`}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#E8A020,#F5C542)" }} />
      </div>
      <ul className="mt-4 space-y-2.5">
        {rows.map((r) => (
          <li key={r.label} className="flex items-center justify-between gap-3">
            <span className={`text-xs ${labelCls}`}>{r.label}</span>
            <span className={`flex items-center gap-1.5 text-xs font-bold ${valueCls}`}>
              {r.detail}
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: BUYBOX_DOT[r.status] }} />
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Members-only contact card (free/Basic buyers) ──────────────────── */
function MembersOnlyContactCard({ agentName, onUpgrade }: { agentName?: string | null; onUpgrade: () => void }) {
  const initials = agentName
    ? agentName.trim().split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()
    : null;
  return (
    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-4 space-y-3">
      <div className="flex items-center gap-3">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-sm font-extrabold"
          style={{ backgroundColor: "#FDF3E0", color: "#8A5B00", border: `1px solid ${GOLD}55` }}
        >
          {initials ?? <Lock className="h-5 w-5" />}
        </div>
        <div className="min-w-0">
          <p className="text-[0.6rem] font-bold uppercase tracking-widest text-gray-400">Listing Advisor</p>
          <p className="text-sm font-extrabold text-gray-900">Members only</p>
        </div>
      </div>
      <p className="flex items-start gap-2 text-xs leading-relaxed text-gray-500">
        <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" style={{ color: GOLD }} />
        <span>
          Messaging the advisor and making offers is a{" "}
          <span className="font-bold" style={{ color: GOLD }}>members-only</span> privilege.
        </span>
      </p>
      <button
        onClick={onUpgrade}
        className="flex w-full items-center justify-center gap-1.5 rounded-xl py-2.5 text-xs font-extrabold shadow-md transition-opacity hover:opacity-90"
        style={{ backgroundColor: GOLD, color: NAVY }}
      >
        Upgrade to contact seller →
      </button>
    </div>
  );
}

/* ── Tier usage meter (advisor card: messages/day, deals/month) ─────── */
function UsageMeter({ label, used, limit }: { label: string; used: number; limit: number | null }) {
  const unlimited = limit == null;
  const pct = unlimited ? 100 : limit <= 0 ? 100 : Math.min(100, Math.round((used / limit) * 100));
  const atLimit = !unlimited && used >= limit;
  return (
    <div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs" style={{ color: "var(--color-muted-foreground)" }}>{label}</span>
        <span className="text-xs font-extrabold" style={{ color: INK }}>
          {used}
          <span style={{ color: "var(--color-muted-foreground)" }}>{unlimited ? " / ∞" : ` / ${limit}`}</span>
        </span>
      </div>
      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: "var(--color-border)" }}>
        <div
          className="h-full rounded-full"
          style={{ width: `${pct}%`, background: atLimit ? "linear-gradient(90deg,#B45309,#E8A020)" : "linear-gradient(90deg,#E8A020,#F5C542)" }}
        />
      </div>
    </div>
  );
}

type TabKey = "overview" | "financials" | "sba" | "valuation" | "model" | "location" | "actions";

export default function ListingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Page that linked here (e.g. /admin/buyer-activity), so "Back" returns there
  // instead of the role's default listings home.
  const backFrom = (location.state as { from?: string } | null)?.from;
  const toast = useToast();
  const carouselRef = useRef<HTMLDivElement>(null);

  const [listing, setListing]         = useState<BusinessListing | null>(null);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState<string | null>(null);
  const [sellerInfo, setSellerInfo]   = useState<UserType | null>(null);
  const [agentInfo, setAgentInfo]     = useState<UserType | null>(null);
  const [categoryName, setCategoryName]     = useState<string | null>(null);
  const [locationLabel, setLocationLabel]   = useState<string | null>(null);
  const [locationLabelShort, setLocationLabelShort] = useState<string | null>(null);
  const [categoryImageUrl, setCategoryImageUrl]     = useState<string | null>(null);
  const [moreListings, setMoreListings] = useState<BusinessListing[]>([]);
  const [moreCategories, setMoreCategories] = useState<LookupItem[]>([]);

  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  // Description opens fully expanded; the Show Less toggle lets users shrink it.
  const [overviewExpanded, setOverviewExpanded] = useState(true);
  const [showAdvisorModal, setShowAdvisorModal] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [alreadyInterested, setAlreadyInterested] = useState(false);
  const [interestId, setInterestId]       = useState<string | null>(null);
  const [interestMessage, setInterestMessage] = useState("");
  const [showInterestForm, setShowInterestForm] = useState(false);
  const [interestLoading, setInterestLoading] = useState(false);
  const [interestSuccess, setInterestSuccess] = useState(false);
  const [pdfLoading, setPdfLoading]   = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const [lockedByMe, setLockedByMe]   = useState(false);
  const [lockUntil, setLockUntil]     = useState<string | null>(null);
  const [tierAllowsLock, setTierAllowsLock] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [favLoading, setFavLoading]   = useState(false);
  const [dealScore, setDealScore]     = useState<DealScoreResponse["deal"]>(null);
  const [usage, setUsage]             = useState<OutreachUsage | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  // Basic-plan unlock state for THIS listing (see isLocked below).
  const [basicUnlocked, setBasicUnlocked]   = useState(false);
  const [basicRemaining, setBasicRemaining] = useState<number | null>(null);
  const [basicUnlocking, setBasicUnlocking] = useState(false);

  // ── Role helpers ──────────────────────────────────────────────────────────
  const isOwner   = user && listing && user.id === listing.seller_id;
  const isAgent   = user && listing && user.id === listing.agent_id;
  // Staff-ness: both admin tiers. A bare hasRole("admin") silently excluded
  // org admins, which is why the Share button vanished for them.
  const isAdmin   = hasRole(...ADMIN_ROLES);
  const isSeller  = hasRole("seller");
  const isBuyer   = hasRole("buyer");
  const isModuleVisible = useModuleVisibility();
  const isAdvisor = hasRole("bl_advisor");
  // Buyers never see the unique-viewer count — how many rivals are circling a
  // deal is staff-side information. Mirrors isBuyerView in Home.tsx.
  const isBuyerOnlyView = isBuyer && !isAdmin && !isAdvisor && !isSeller;
  const canSeeFullDetails = isOwner || isAgent || isAdmin || (isSeller && isOwner);
  // Free (Basic) buyers: buyer role without an active subscription. They can
  // browse the whole marketplace, but member content (full narrative, advisor
  // contact, exact location, offers) shows Upgrade CTAs → /choose-plan.
  const isFreeBuyer = isFreeBuyerUser(user);
  // A Basic buyer holds a small WEEKLY allowance of full-detail unlocks
  // (tier_service.BASIC_FREE_UNLOCKS). Spending one on THIS listing makes the
  // API return it unredacted AND opens the advisor chat / offer (and so the NDA
  // flow) for it — those key off the per-listing unlock (isLocked). The other
  // member actions (favorites, locks, SBA calc) stay on isFreeBuyer.
  const isLocked = isFreeBuyer && !basicUnlocked;
  const canSeeContent     = canSeeFullDetails || (isBuyer && !isLocked) || isAdvisor;
  // Only active listings (status 3) are open for buyer outreach. Once a listing
  // moves into LOI / Under Offer / Due Diligence / Sold the deal is in progress,
  // so other buyers shouldn't be able to contact the advisor or express interest.
  const isActiveListing = listing?.listing_status_id === 3;
  const canExpressInterest = isActiveListing && !isLocked && (!user || Boolean(isBuyer && !isOwner && !isAgent && !isAdmin && !isSeller && !isAdvisor));
  // Buyers/visitors only see the advisor contact card on active listings; staff
  // (owner/agent/admin/advisor) keep it on every status to manage the deal.
  const canSeeAdvisorCard  = Boolean(isActiveListing || canSeeFullDetails || isAdvisor);
  const canUseListingLock  = Boolean(user && isBuyer && !isOwner && !isAgent && !isAdmin && isActiveListing);
  const canFavorite        = Boolean(user && !isAdmin && !isOwner && !isAgent);
  // "Admin with authority over THIS listing" — a platform admin always, an org
  // admin only on its own organization's listings. Viewing is shared across
  // organizations; acting is not. Separate from isAdmin, which is staff-ness.
  const adminHere          = isAdmin && canActOnListingOrg(user, listing?.org_id);
  const canEdit            = Boolean(user && (isOwner || isAgent || adminHere) && (adminHere || listing?.listing_status_id === 1));
  // Guest sharing is a BL Advisor / admin tool, only for active listings. Either
  // admin tier (incl. an org admin, e.g. ECFA) may share any listing — sharing
  // distributes, it doesn't modify — while advisors share only what they manage.
  // The backend enforces this (routers/listing_shares.py); this just hides the button.
  const canShare           = Boolean(user && (isAdvisor || isAdmin) && isActiveListing);
  // Buyer has spent the day's advisor-outreach allowance (Explorer = 5/day).
  const outOfMessages = usage?.messages_limit != null && (usage.messages_remaining ?? 0) <= 0;

  // Role-aware listings home so "Back to listings" returns to the page that
  // carries the sidebar and restores the saved listings page (e.g. page 5).
  const listingsHomeUrl = backFrom
    ? backFrom
    : isAdmin
    ? "/admin/home"
    : isAdvisor
    ? "/bl-advisor/home"
    : isSeller
    ? "/seller/home"
    : isBuyer
    ? "/buyer/home"
    : "/";

  // ── Data fetching ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!canUseListingLock) { setTierAllowsLock(false); return; }
    api.get<{ has_tier: boolean; tier: { listing_locks_allowed: number | null; lock_duration_hours: number | null } | null }>("/tiers/current")
      .then((d) => setTierAllowsLock(Boolean(d?.has_tier && d.tier && (d.tier.listing_locks_allowed ?? 0) > 0 && (d.tier.lock_duration_hours ?? 0) > 0)))
      .catch(() => setTierAllowsLock(false));
  }, [canUseListingLock]);

  useEffect(() => {
    if (!canUseListingLock || !id) { setLockedByMe(false); setLockUntil(null); return; }
    api.get<{ is_locked_by_me: boolean; locked_until: string | null }>(`/tiers/lock-status/${id}`)
      .then((d) => { setLockedByMe(Boolean(d.is_locked_by_me)); setLockUntil(d.is_locked_by_me ? d.locked_until : null); })
      .catch(() => { setLockedByMe(false); setLockUntil(null); });
  }, [canUseListingLock, id]);

  // Buy-box fit scorecard (buyers with a saved BuyBox only, while the AI Deal
  // Matching module is on — the endpoint 403s when it's switched off)
  const dealMatchingOn = isModuleVisible("ai_deal_matching");
  useEffect(() => {
    if (!id || !isBuyer || !dealMatchingOn || !user?.buyer_preferences || user.needs_buyer_preferences) {
      setDealScore(null);
      return;
    }
    api.get<DealScoreResponse>(`/business-listings/${id}/deal-score`)
      .then((d) => setDealScore(d.has_preferences ? d.deal : null))
      .catch(() => setDealScore(null));
  }, [id, isBuyer, dealMatchingOn, user?.buyer_preferences, user?.needs_buyer_preferences]);

  useEffect(() => {
    if (!id) return;
    api.get<BusinessListing>(`/business-listings/${id}`)
      .then(setListing).catch((e) => setError(e.message)).finally(() => setLoading(false));
    // Track view for authenticated users (fire-and-forget)
    if (user) {
      api.post(`/business-listings/${id}/view`, {}).catch(() => {});
    }
  }, [id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Basic buyers: has this listing already been unlocked, and how many of this
  // week's allowance are left? Drives the Unlock / Upgrade choice below.
  useEffect(() => {
    if (!id || !isFreeBuyer) { setBasicUnlocked(false); setBasicRemaining(null); return; }
    api
      .get<{ free_unlocks_remaining: number | null; unlocked_listing_ids: string[] }>("/tiers/my-unlocked-listings")
      .then((d) => {
        setBasicUnlocked((d.unlocked_listing_ids || []).includes(id));
        setBasicRemaining(d.free_unlocks_remaining);
      })
      .catch(() => { setBasicUnlocked(false); setBasicRemaining(null); });
  }, [id, isFreeBuyer]);

  /** Spend one Basic unlock on this listing, then re-fetch it unredacted. */
  const handleBasicUnlock = async () => {
    if (!id || basicUnlocking) return;
    if (basicRemaining !== null && basicRemaining <= 0) { navigate("/choose-plan"); return; }
    setBasicUnlocking(true);
    try {
      const res = await api.post<{ success: boolean; free_remaining: number | null }>(
        `/tiers/free-unlock/${id}`,
        {}
      );
      if (res.success) {
        setBasicUnlocked(true);
        setBasicRemaining(res.free_remaining);
        // The member fields were stripped server-side on the first load, so the
        // page needs the listing again now that the unlock is on record.
        const fresh = await api.get<BusinessListing>(`/business-listings/${id}`);
        setListing(fresh);
        toast.success("Deal unlocked");
      }
    } catch (err) {
      // 402 = allowance spent (a second tab, or a stale count): the only way on
      // is a plan.
      if (err instanceof ApiError && err.status === 402) {
        setBasicRemaining(0);
        navigate("/choose-plan");
      } else {
        toast.error(err instanceof Error ? err.message : "Unable to unlock this deal.");
      }
    } finally {
      setBasicUnlocking(false);
    }
  };

  /** Gold CTA on every locked panel: spend an unlock while any remain, else upgrade. */
  const renderUnlockCta = (className: string) => {
    const canUnlock = (basicRemaining ?? 0) > 0;
    return (
      <button
        onClick={canUnlock ? handleBasicUnlock : () => navigate("/choose-plan")}
        disabled={basicUnlocking}
        className={className}
        style={{ backgroundColor: GOLD, color: NAVY }}
      >
        {basicUnlocking
          ? "Unlocking…"
          : canUnlock
          ? `Unlock this deal (${basicRemaining} left)`
          : "Upgrade →"}
      </button>
    );
  };

  // Reset per-listing UI when navigating between listings (carousel links)
  useEffect(() => {
    setActiveTab("overview");
    setOverviewExpanded(true);
    setShowInterestForm(false);
    setInterestSuccess(false);
  }, [id]);

  useEffect(() => {
    if (!listing?.industry_category_id) return;
    api.get<LookupItem[]>("/lookups/categories")
      .then((cats) => {
        setMoreCategories(cats);
        const cat = cats.find((c) => c.id === listing.industry_category_id);
        if (cat) setCategoryName(cat.name);
      }).catch(() => {});
  }, [listing]);

  useEffect(() => {
    if (!listing) return;
    const { country_id, state_id, city_id, city_name, state_name, country_name } = listing;
    if (city_name || state_name || country_name) {
      setLocationLabel([city_name, state_name, country_name].filter(Boolean).join(", "));
      setLocationLabelShort([city_name, state_name].filter(Boolean).join(", "));
      return;
    }
    if (!country_id && !state_id && !city_id) return;
    Promise.all([
      city_id    ? api.get<LookupItem[]>(`/lookups/cities?state_id=${state_id}`).then((cs) => cs.find((c) => c.id === city_id)?.name ?? null).catch(() => null)   : Promise.resolve(null),
      state_id   ? api.get<LookupItem[]>(`/lookups/states?country_id=${country_id}`).then((ss) => ss.find((s) => s.id === state_id)?.name ?? null).catch(() => null) : Promise.resolve(null),
      country_id ? api.get<LookupItem[]>("/lookups/countries").then((cs) => cs.find((c) => c.id === country_id)?.name ?? null).catch(() => null)                     : Promise.resolve(null),
    ]).then(([city, state, country]) => {
      setLocationLabel([city, state, country].filter(Boolean).join(", "));
      setLocationLabelShort([city, state].filter(Boolean).join(", "));
    });
  }, [listing]);

  useEffect(() => {
    setCategoryImageUrl(listing?.listing_image_url ? assetUrl(listing.listing_image_url) : null);
  }, [listing]);

  useEffect(() => {
    if (!listing || !user || !isAdmin) return;
    if (listing.seller_id !== listing.agent_id)
      api.get<UserType>(`/admin/users/${listing.seller_id}`).then(setSellerInfo).catch(() => {});
    if (listing.agent_id)
      api.get<UserType>(`/admin/users/${listing.agent_id}`).then(setAgentInfo).catch(() => {});
  }, [listing, user]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!id || !user) return;
    api.get<{ interested: boolean; interest_id: string | null }>(`/interests/check/${id}`)
      .then((res) => { setAlreadyInterested(res.interested); setInterestId(res.interest_id); }).catch(() => {});
  }, [id, user]);

  // Daily-outreach / monthly-unlock usage for the advisor card meters. Only
  // member buyers have a metered tier; everyone else clears it (meters hidden).
  const refreshUsage = useCallback(() => {
    if (!user || !isBuyer || isFreeBuyer) { setUsage(null); return; }
    api.get<OutreachUsage>("/tiers/outreach-usage")
      .then((d) => setUsage(d && d.has_tier ? d : null))
      .catch(() => setUsage(null));
  }, [user, isBuyer, isFreeBuyer]);

  useEffect(() => { refreshUsage(); }, [refreshUsage]);

  // The advisor chat fires "outreach-used" after a buyer sends a reply so the
  // "Messages today" meter here reflects the spend without a page reload.
  useEffect(() => {
    const onUsed = () => refreshUsage();
    window.addEventListener("outreach-used", onUsed);
    return () => window.removeEventListener("outreach-used", onUsed);
  }, [refreshUsage]);

  // Fetch favorite status
  useEffect(() => {
    if (!id || !canFavorite) { setIsFavorited(false); return; }
    api.get<{ ids: string[] }>("/favorites/ids")
      .then((data) => setIsFavorited(data.ids.includes(String(id))))
      .catch(() => {});
  }, [id, canFavorite]);

  // Fetch "Explore More" listings
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams({ page: "1", page_size: "7", listing_status_id: "3" });
    api.get<{ data: BusinessListing[] }>(`/business-listings/?${params}`)
      .then((res) => setMoreListings(res.data.filter((l) => l.id !== id).slice(0, 6)))
      .catch(() => {});
  }, [id, user]);

  // ── Actions ───────────────────────────────────────────────────────────────
  const handleExportPDF = async () => {
    if (!listing) return;
    setPdfLoading(true);
    try {
      const imageUrl = assetUrl(listing.listing_image_url) ?? undefined;
      const cf_multiple = listing.asking_price && listing.ebitda ? listing.asking_price / listing.ebitda : null;
      await downloadListingPDF({
        title: listing.title, categoryName, overview: listing.overview, locationLabel,
        address_line: listing.address_line, zip_code: listing.zip_code,
        asking_price: listing.asking_price, revenue: listing.revenue, ebitda: listing.ebitda, cf_multiple,
        established_year: listing.established_year, established_month: listing.established_month,
        website_url: canSeeFullDetails ? listing.website_url : null, is_sba_approved: listing.is_sba_approved,
        seller_financing: listing.seller_financing ?? false,
        additional_notes: listing.additional_notes, agent_notes: listing.agent_notes,
        agent_commission_pct: listing.agent_commission_pct,
        seller_name: listing.seller_name, seller_email: listing.seller_email, seller_phone: listing.seller_phone,
        agent_name: listing.agent_name, agent_email: listing.agent_email, agent_phone: listing.agent_phone,
        agent_avatar_url: assetUrl(listing.agent_avatar_url) ?? undefined,
        imageUrl,
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to generate PDF");
    } finally { setPdfLoading(false); }
  };

  const handleToggleFavorite = async () => {
    if (!user) { navigate("/sign-in"); return; }
    // Favorites are a member feature — the API rejects free buyers with 402,
    // so send them to plan selection instead.
    if (isFreeBuyer) { navigate("/choose-plan"); return; }
    if (!id || favLoading) return;
    setFavLoading(true);
    try {
      // Explicit add/remove so the result always matches what the button showed.
      const res = isFavorited
        ? await api.delete<{ favorited: boolean }>(`/favorites/${id}`)
        : await api.put<{ favorited: boolean }>(`/favorites/${id}`, {});
      setIsFavorited(res.favorited);
      toast.success(res.favorited ? "Added to favorites" : "Removed from favorites");
    } catch {
      toast.error("Failed to update favorites");
    } finally { setFavLoading(false); }
  };

  const handleShowInterest = async () => {
    if (!user) { navigate("/sign-in"); return; }
    if (!id) return;
    setInterestLoading(true);
    try {
      const res = await api.post<{ id: string; acknowledgment?: string | null }>("/interests/", { listing_id: id, message: interestMessage || undefined });
      setAlreadyInterested(true); setInterestSuccess(true); setShowInterestForm(false);
      setInterestId(res.id);
      // Niva's auto-acknowledgment; the same text opens the interest thread.
      if (res.acknowledgment) toast.success(`Niva: ${res.acknowledgment}`);
      refreshUsage();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to submit interest");
      // A 429 (daily limit) still consumed the day's allowance server-side; sync
      // the meter so it shows the buyer is out of messages.
      if (err instanceof ApiError && err.status === 429) refreshUsage();
    } finally { setInterestLoading(false); }
  };

  // "Message advisor" — open the existing thread's chat if the buyer has already
  // expressed interest, otherwise start one via the express-interest form.
  const handleMessageAdvisor = () => {
    if (!user) { navigate("/sign-in"); return; }
    if (alreadyInterested && interestId) { window.dispatchEvent(new Event("open-interest-chat")); return; }
    if (canExpressInterest) setShowInterestForm(true);
  };

  const handleLockListing = async () => {
    if (!id || !user) { navigate("/sign-in"); return; }
    setLockLoading(true);
    try {
      const result = await api.post<{ locked_until: string; message: string }>(`/tiers/lock-listing/${id}`, {});
      setLockedByMe(true); setLockUntil(result.locked_until);
      toast.success(result.message || "Listing locked successfully");
    } catch (error: unknown) {
      const message = error instanceof ApiError ? error.message : "Unable to lock listing";
      if (message.toLowerCase().includes("already locked by you")) setLockedByMe(true);
      toast.error(message);
    } finally { setLockLoading(false); }
  };

  const handleReleaseLock = async () => {
    if (!id || !user) { navigate("/sign-in"); return; }
    setLockLoading(true);
    try {
      const result = await api.delete<{ message: string }>(`/tiers/lock-listing/${id}`);
      setLockedByMe(false); setLockUntil(null);
      toast.success(result.message || "Listing lock released");
    } catch (error: unknown) {
      toast.error(error instanceof ApiError ? error.message : "Unable to release listing lock");
    } finally { setLockLoading(false); }
  };

  const scrollCarousel = (dir: "left" | "right") => {
    if (!carouselRef.current) return;
    carouselRef.current.scrollBy({ left: dir === "right" ? 320 : -320, behavior: "smooth" });
  };

  // ── Loading / error ───────────────────────────────────────────────────────
  if (loading) return <div className="max-w-4xl mx-auto px-4 py-20"><Loader /></div>;
  if (error || !listing) return (
    <div className="max-w-4xl mx-auto px-4 py-20 text-center">
      <p className="text-destructive mb-4 text-lg font-medium">{error || "Listing not found"}</p>
      <Link to={listingsHomeUrl} state={{ scrollToListings: true }}>
        <Button variant="outline">Back to listings</Button>
      </Link>
    </div>
  );

  // ── Derived deal metrics ──────────────────────────────────────────────────
  const publicDesc  = listing.display_overview || listing.overview;
  const fullDesc    = listing.overview;
  const shownDesc   = canSeeContent ? fullDesc : publicDesc;
  const cfMultRaw   = listing.asking_price && listing.ebitda && listing.ebitda !== 0
    ? listing.asking_price / listing.ebitda : null;
  const cfMultiple  = cfMultRaw != null ? cfMultRaw.toFixed(2) + "X" : null;
  const marginPct   = listing.revenue && listing.ebitda && listing.revenue > 0
    ? (listing.ebitda / listing.revenue) * 100 : null;
  const age         = listing.established_year ? new Date().getFullYear() - listing.established_year : null;
  const dscrEst     = listing.asking_price && listing.ebitda ? estimateDscr(listing.asking_price, listing.ebitda) : null;
  const isNew       = listing.published_at
    ? Date.now() - new Date(listing.published_at).getTime() < 14 * 24 * 3600 * 1000 : false;
  const viewCount   = listing.view_count ?? 0;

  const displayTitle = (canSeeFullDetails || user) ? listing.title : (categoryName || "Business Listing");

  // BL Score is shown to authenticated users (mirrors old risk-score gating)
  const blResult = user ? computeBlScore(listing) : null;
  // Buy-box fit for buyers with completed preferences (fetched scorecard)
  const buyBox = dealScore
    ? {
        pct: dealScore.fit_score,
        rows: dealScore.metrics.map((m) => ({
          label: m.label,
          status: metricStatus(m.value),
          detail: `${m.value}/100`,
        })),
      }
    : null;

  const mapQuery = encodeURIComponent(
    [canSeeFullDetails ? listing.address_line : null, locationLabel].filter(Boolean).join(", ") || "United States"
  );

  // ── Deal brief narrative + signal pills (from listing data only) ──────────
  const briefParts: string[] = [];
  briefParts.push(
    age
      ? `An established ${age}-year ${categoryName ? categoryName.toLowerCase() : "business"} operation${locationLabelShort ? ` based in ${locationLabelShort}` : ""}.`
      : `${categoryName ?? "Business"} acquisition opportunity${locationLabelShort ? ` based in ${locationLabelShort}` : ""}.`
  );
  if (listing.revenue && listing.ebitda) {
    briefParts.push(`Generating ${fmt(listing.revenue)} in annual revenue with ${fmt(listing.ebitda)} EBITDA${marginPct != null ? ` (${marginPct.toFixed(1)}% margin)` : ""}${cfMultRaw != null ? `, offered at a ${cfMultRaw.toFixed(1)}× cash-flow multiple` : ""}.`);
  } else if (listing.asking_price) {
    briefParts.push(`Offered at ${fmt(listing.asking_price)}${listing.make_offer ? " (open to offers)" : ""}.`);
  }
  if (listing.is_sba_approved || listing.seller_financing) {
    const fin = [listing.is_sba_approved && "SBA pre-approval", listing.seller_financing && "seller financing"].filter(Boolean).join(" and ");
    briefParts.push(`Financing is de-risked with ${fin} on the table.`);
  }
  const brief = briefParts.join(" ");

  const positives: string[] = [];
  if (cfMultRaw != null && cfMultRaw < 4) positives.push(`${cfMultRaw.toFixed(1)}× cash-flow multiple`);
  if (marginPct != null && marginPct >= 15) positives.push(`${marginPct.toFixed(0)}% EBITDA margin`);
  if (listing.is_sba_approved)  positives.push("SBA pre-approved");
  if (listing.seller_financing) positives.push("Seller financing available");
  if (listing.agent_id)         positives.push("Vetted by a BL Advisor");
  if (age != null && age >= 5)  positives.push(`${age} years established`);

  const watches: string[] = [];
  if (listing.revenue == null || listing.ebitda == null) watches.push("Limited financial disclosure");
  if (cfMultRaw != null && cfMultRaw > 5) watches.push("Elevated asking multiple");
  if (age != null && age < 2) watches.push("Early-stage business");
  if (!listing.agent_id) watches.push("No advisor assigned");

  // ── Tabs (data-gated: a tab only renders when it has something to show) ───
  const actionCount = [canExpressInterest && !alreadyInterested, canUseListingLock && tierAllowsLock, canFavorite && !isFreeBuyer, true /* PDF */, canEdit]
    .filter(Boolean).length;
  const tabs: { key: TabKey; label: string; Icon: typeof LayoutGrid; show: boolean; badge?: number }[] = [
    { key: "overview",   label: "Overview & Score", Icon: LayoutGrid,    show: true },
    { key: "financials", label: "Financials",       Icon: LineChart,     show: Boolean(listing.revenue || listing.ebitda || listing.asking_price) },
    { key: "sba",        label: "SBA Calc",         Icon: Calculator,    show: Boolean(isBuyer && !isFreeBuyer) },
    { key: "valuation",  label: "Valuation",        Icon: Scale,         show: Boolean(isBuyer && !isFreeBuyer && (listing.ebitda || listing.revenue)) },
    // Needs both lines: the model grows revenue and applies the EBITDA margin.
    { key: "model",      label: "5-Yr Model",       Icon: TrendingUp,    show: Boolean(isBuyer && !isFreeBuyer && isModuleVisible("financial_model") && listing.revenue && listing.ebitda && listing.revenue > 0) },
    { key: "location",   label: "Location",         Icon: MapPin,        show: Boolean(locationLabel || listing.address_line) },
    { key: "actions",    label: "Deal Actions",     Icon: ClipboardList, show: Boolean(user), badge: actionCount },
  ];
  const visibleTabs = tabs.filter((t) => t.show);
  const currentTab: TabKey = visibleTabs.some((t) => t.key === activeTab) ? activeTab : "overview";

  return (
    <>
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-4 sm:px-6 lg:px-8 pt-5">

        {/* ── Top action row ─────────────────────────────────────────── */}
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
          <Link
            to={listingsHomeUrl}
            state={{ scrollToListings: true }}
            className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white border border-gray-200 text-gray-700 text-xs font-semibold shadow-sm hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            <span>Back to listings</span>
          </Link>
          <div className="flex flex-wrap items-center gap-2">
            {canFavorite && (
              <button
                onClick={handleToggleFavorite}
                disabled={favLoading}
                title={isFavorited ? "Remove from favorites" : "Add to favorites"}
                aria-label={isFavorited ? "Remove from favorites" : "Add to favorites"}
                className="flex h-9 w-9 items-center justify-center rounded-xl bg-white border border-gray-200 shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-60"
              >
                <Heart className={`h-4 w-4 transition-colors ${isFavorited ? "fill-rose-500 text-rose-500" : "text-gray-600"}`} />
              </button>
            )}
            <button
              onClick={handleExportPDF}
              disabled={pdfLoading}
              title="Export PDF"
              className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-white border border-gray-200 text-gray-700 text-xs font-semibold shadow-sm hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              {pdfLoading ? (
                <><span className="h-3.5 w-3.5 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" /><span>Generating…</span></>
              ) : (
                <><Download className="h-3.5 w-3.5" /><span>Export PDF</span></>
              )}
            </button>
            {canShare && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 rounded-xl text-xs h-9"
                onClick={() => setShowShareModal(true)}
              >
                <Share2 className="h-3.5 w-3.5" />
                Share
              </Button>
            )}
            {/* No-login card for pasting into inquiry replies — no recipient
                name/email needed. Shows only what /public/deals serves. /d/
                (not /deal/) so the link unfurls with a preview in WhatsApp etc. */}
            {canShare && (
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5 rounded-xl text-xs h-9"
                title="Copy a public link that anyone can open without signing in"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(`${window.location.origin}/d/${listing.id}`);
                    toast.success("Public link copied");
                  } catch {
                    toast.error("Couldn't copy the link");
                  }
                }}
              >
                <Link2 className="h-3.5 w-3.5" />
                Copy public link
              </Button>
            )}
            {canEdit && (
              <Link to={`/listings/${listing.id}/edit`}>
                <Button variant="outline" size="sm" className="gap-1.5 rounded-xl text-xs h-9">
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </Button>
              </Link>
            )}
          </div>
        </div>

        {showShareModal && listing && (
          <ShareListingModal
            listingId={listing.id}
            listingTitle={listing.title}
            onClose={() => setShowShareModal(false)}
          />
        )}

        {/* ── Header banner — warm-dark gradient ──────────────────────── */}
        <div className="relative overflow-hidden rounded-2xl shadow-md"
          style={{ background: `linear-gradient(120deg, ${NAVY} 0%, ${NAVY2} 60%, #12100a 100%)` }}>
          {/* decorative gold glow */}
          <div className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full"
            style={{ background: "radial-gradient(circle, rgba(212,169,62,0.28), transparent 70%)" }} />

          {/* Thin photo banner — image runs full width above the title;
              click opens the full photo. */}
          {categoryImageUrl && (
            <button
              type="button"
              onClick={() => setShowPhotoModal(true)}
              title="View photo"
              aria-label="View full photo"
              className="group relative block h-36 w-full overflow-hidden sm:h-44 focus:outline-none"
            >
              <img src={categoryImageUrl} alt={displayTitle}
                className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
              {/* soft fade into the dark title row below */}
              <span className="pointer-events-none absolute inset-0 bg-gradient-to-b from-black/10 via-transparent to-black/50" />
              <span className="absolute bottom-3 right-3 flex items-center gap-1.5 rounded-full bg-black/70 px-3 py-1 text-[0.65rem] font-bold text-white backdrop-blur-sm">
                <Maximize2 className="h-3 w-3" />
                View photo
              </span>
            </button>
          )}

          <div className="relative flex flex-col sm:flex-row sm:items-center gap-4 p-4 sm:p-5">
            {/* Badges + title + meta */}
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {isNew && (
                  <span className="rounded-full px-2.5 py-0.5 text-[0.58rem] font-extrabold uppercase tracking-widest text-white" style={{ backgroundColor: ORANGE }}>
                    New Listing
                  </span>
                )}
              </div>
              <h1 className="mt-1.5 text-xl sm:text-2xl font-extrabold text-white leading-tight">{displayTitle}</h1>
              <p className="mt-1.5 text-xs sm:text-sm flex flex-wrap items-center gap-x-2 gap-y-0.5" style={{ color: "rgba(200,215,240,0.85)" }}>
                {[
                  locationLabelShort,
                  listing.established_year ? `Est. ${listing.established_year}` : null,
                  age ? `${age} yrs` : null,
                  categoryName,
                ].filter(Boolean).map((part, i) => (
                  <span key={i} className="flex items-center gap-2">
                    {i > 0 && <span className="text-white/30">·</span>}
                    {part}
                  </span>
                ))}
                {/* Unique viewers. Counts people rather than visits, so the
                    label says "viewer(s)", and it's hidden at 0 rather than
                    advertising that nobody has opened the deal. The current
                    visit lands via the fire-and-forget POST above, so a
                    first-time viewer sees the count from just before they
                    arrived — it catches up on the next load. */}
                {!isBuyerOnlyView && viewCount > 0 && (
                  <span className="flex items-center gap-2">
                    <span className="text-white/30">·</span>
                    <span className="flex items-center gap-1.5" title={`${viewCount.toLocaleString()} unique ${viewCount === 1 ? "buyer has" : "buyers have"} viewed this listing`}>
                      <Eye className="h-3.5 w-3.5 shrink-0 opacity-80" />
                      {viewCount.toLocaleString()} {viewCount === 1 ? "viewer" : "viewers"}
                    </span>
                  </span>
                )}
              </p>
            </div>

            {/* Score chips — glass on dark banner. The reference code rides
                alongside them as its own tag instead of sitting in the meta row. */}
            <div className="flex flex-wrap items-center gap-2 shrink-0">
              {listing.reference_code && (
                <span className="flex flex-col items-center rounded-xl border px-3.5 py-2.5 backdrop-blur-sm"
                  style={{ borderColor: "rgba(212,169,62,0.45)", backgroundColor: "rgba(212,169,62,0.16)" }}>
                  <span className="font-mono text-base font-extrabold leading-none tracking-widest" style={{ color: "#FCD34D" }}>
                    {listing.reference_code}
                  </span>
                  <span className="mt-1 text-[0.52rem] font-bold uppercase tracking-widest text-white/60">Listing Code</span>
                </span>
              )}
              {blResult && (
                <div className="flex flex-col items-center rounded-xl border border-white/15 bg-white/10 px-4 py-2.5 backdrop-blur-sm">
                  <span className="text-2xl font-extrabold leading-none" style={{ color: "#FCD34D" }}>{blResult.score}</span>
                  <span className="mt-1 text-[0.52rem] font-bold uppercase tracking-widest text-white/60">BL Score</span>
                </div>
              )}
              {buyBox && (
                <div className="flex flex-col items-center rounded-xl border border-white/15 bg-white/10 px-4 py-2.5 backdrop-blur-sm">
                  <span className="text-2xl font-extrabold leading-none" style={{ color: "#FCD34D" }}>{buyBox.pct}%</span>
                  <span className="mt-1 text-[0.52rem] font-bold uppercase tracking-widest text-white/60">Buy-Box Fit</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Deal Brief panel ────────────────────────────────────────── */}
        <div className="mt-4 rounded-3xl overflow-hidden shadow-md"
          style={{ background: `linear-gradient(120deg, ${NAVY} 0%, #1c160b 55%, #110f09 100%)` }}>
          {/* Right column is a fixed 22rem so the buy-box card stays compact
              instead of stretching with the page. */}
          <div className={`grid grid-cols-1 gap-6 p-5 sm:p-7 ${buyBox || blResult ? "lg:grid-cols-[1fr_22rem]" : ""}`}>
            {/* Narrative + signal pills */}
            <div>
              <div className="flex flex-wrap items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl" style={{ background: "rgba(212,169,62,0.3)" }}>
                  {/* Pinned hex, not text-amber-200: this panel is always dark,
                      and the dark theme remaps amber-200 to a near-black tone
                      that made the icon invisible. */}
                  <Sparkles className="h-4 w-4" style={{ color: "#FCD34D" }} />
                </div>
                <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight text-white">Deal Brief</h2>
                <span className="rounded-full border border-white/20 bg-white/10 px-2.5 py-0.5 text-[0.58rem] font-semibold uppercase tracking-widest text-white/70">
                  Generated from listing data
                </span>
              </div>

              <p className="mt-4 text-sm sm:text-[0.95rem] leading-6 sm:leading-7 text-white/85">{brief}</p>

              <div className="mt-4 flex flex-wrap gap-2">
                {positives.slice(0, 4).map((p) => (
                  <span key={p} className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold"
                    style={{ borderColor: "rgba(74,222,128,0.4)", backgroundColor: "rgba(22,163,74,0.18)", color: "#86EFAC" }}>
                    <CheckCircle className="h-3.5 w-3.5" />
                    {p}
                  </span>
                ))}
                {watches.slice(0, 2).map((w) => (
                  <span key={w} className="flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-semibold"
                    style={{ borderColor: "rgba(251,191,36,0.4)", backgroundColor: "rgba(232,160,32,0.16)", color: "#FCD34D" }}>
                    <AlertTriangle className="h-3.5 w-3.5" />
                    {w}
                  </span>
                ))}
              </div>
            </div>

            {/* Right panel — buy-box fit (buyers) or BL score summary */}
            {buyBox ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.07] p-5">
                <BuyBoxPanel pct={buyBox.pct} rows={buyBox.rows} dark />
              </div>
            ) : blResult ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.07] p-5">
                <p className="text-[0.6rem] font-bold uppercase tracking-widest text-white/50">BL Score™ breakdown</p>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-4xl font-extrabold text-white">{blResult.score}</span>
                  <span className="text-xs font-bold text-white/50">/ 100</span>
                </div>
                <ul className="mt-4 space-y-2.5">
                  {blResult.categories.map((c) => (
                    <li key={c.label} className="flex items-center justify-between gap-3">
                      <span className="text-xs text-white/70">{c.label}</span>
                      <span className="flex items-center gap-1.5 text-xs font-bold text-white">
                        {c.score}
                        <span className="h-2 w-2 rounded-full" style={{ backgroundColor: scoreColor(c.score) }} />
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        </div>

        {/* ── Sticky header: metrics bar + tab row pinned below the navbar ─ */}
        <div className="sticky top-16 z-30 mt-4 -mx-4 sm:-mx-6 lg:-mx-8 bg-white/95 backdrop-blur border-b border-gray-200 shadow-sm">
          <div className="px-4 sm:px-6 lg:px-8">
            {/* Metrics row */}
            {(() => {
              const cells = [
                listing.asking_price && { label: "Asking Price", value: fmt(listing.asking_price), sub: cfMultiple ? `${cfMultRaw!.toFixed(1)}× EBITDA` : listing.make_offer ? "Open to offers" : null },
                listing.revenue      && { label: "Annual Revenue", value: fmt(listing.revenue), sub: null },
                listing.ebitda       && { label: "EBITDA", value: fmt(listing.ebitda), sub: marginPct != null ? `${marginPct.toFixed(1)}% margin` : null },
                dscrEst              && { label: "Est. DSCR", value: `${dscrEst.dscr.toFixed(2)}×`, sub: dscrEst.dscr >= SBA_MIN_DSCR ? "SBA range" : "Below SBA min" },
                listing.established_year && { label: "Established", value: String(listing.established_year), sub: age ? `${age} yrs` : null },
              ].filter(Boolean) as { label: string; value: string; sub: string | null }[];
              if (!cells.length) return null;
              return (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-b border-gray-100">
                  {cells.map((c, i) => (
                    <div key={c.label} className={`px-4 py-3 ${i > 0 ? "border-l border-gray-100" : ""}`}>
                      <p className="text-[0.58rem] font-bold uppercase tracking-widest text-gray-400">{c.label}</p>
                      <p className="mt-0.5 text-lg sm:text-2xl font-extrabold tracking-tight leading-tight" style={{ color: INK }}>{c.value}</p>
                      {c.sub && <p className="text-[0.65rem] text-gray-400">{c.sub}</p>}
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* Tab row */}
            <div className="flex items-center gap-1 overflow-x-auto" style={{ scrollbarWidth: "none" }}>
              {visibleTabs.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTab(t.key)}
                  className={`flex shrink-0 items-center gap-2 border-b-2 px-3.5 py-3 text-sm font-semibold transition-colors ${
                    currentTab === t.key ? "" : "border-transparent text-gray-500 hover:text-gray-800"
                  }`}
                  style={currentTab === t.key ? { borderColor: GOLD, color: GOLD } : undefined}
                >
                  <t.Icon className="h-4 w-4" />
                  {t.label}
                  {t.badge != null && t.badge > 0 && (
                    <span className="flex h-[1.125rem] min-w-[1.125rem] items-center justify-center rounded-full px-1 text-[0.6rem] font-extrabold text-white" style={{ backgroundColor: ORANGE }}>
                      {t.badge}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* ── Tab content ─────────────────────────────────────────────── */}
        {(() => {
          const showSidebar = currentTab === "overview" || currentTab === "financials" || currentTab === "location";

          const highlights = [
            listing.revenue          && { label: "Annual Revenue", value: fmt(listing.revenue),      accent: GOLD,  Icon: TrendingUp  },
            listing.ebitda           && { label: "EBITDA",         value: fmt(listing.ebitda),       accent: GREEN, Icon: BarChart3   },
            cfMultiple               && { label: "C.F. Multiple",  value: cfMultiple,                accent: TEAL,  Icon: TrendingUp  },
            listing.asking_price     && { label: "Asking Price",   value: fmt(listing.asking_price), accent: RED,   Icon: DollarSign  },
            listing.established_year && { label: "Established",    value: String(listing.established_year), accent: GOLD, Icon: Calendar   },
            locationLabelShort       && { label: "Location",       value: locationLabelShort,        accent: GREY,  Icon: MapPin      },
            categoryName             && { label: "Industry",       value: categoryName,              accent: GOLD,  Icon: Building2   },
            listing.is_sba_approved  && { label: "Financing",      value: "SBA Pre-Approved",        accent: GREEN, Icon: CheckCircle },
            listing.seller_financing && { label: "Seller Fin.",    value: "Available",               accent: GREEN, Icon: CheckCircle },
          ].filter(Boolean) as { label: string; value: string; accent: string; Icon: typeof TrendingUp }[];

          return (
          <div className={`mt-5 pb-12 grid grid-cols-1 gap-5 items-start ${showSidebar ? "lg:grid-cols-[1fr_320px]" : ""}`}>
            <div className="min-w-0 space-y-5">

              {/* ═══ OVERVIEW TAB ═══ */}
              {currentTab === "overview" && (
                <>
                  {/* The Opportunity */}
                  {shownDesc && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
                        <div className="h-6 w-1 rounded-full" style={{ backgroundColor: GOLD }} />
                        <h2 className="section-title text-lg text-foreground">The Opportunity</h2>
                      </div>
                      <div className="p-5">
                        <p className={`text-gray-700 leading-7 whitespace-pre-line text-base ${overviewExpanded ? "" : "line-clamp-4"}`}>
                          {shownDesc}
                        </p>
                        {canSeeContent && listing.display_overview && listing.display_overview !== fullDesc && overviewExpanded && (
                          <div className="mt-4 p-3 bg-gray-50 rounded-xl border border-gray-200">
                            <p className="text-base font-bold text-gray-400 uppercase tracking-wider mb-2">Public Description</p>
                            <p className="text-base text-gray-600 leading-7 whitespace-pre-line">{listing.display_overview}</p>
                          </div>
                        )}
                        {shownDesc.length > 240 && (
                          <button
                            onClick={() => setOverviewExpanded((v) => !v)}
                            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold transition-colors hover:underline"
                            style={{ color: GOLD }}
                          >
                            {overviewExpanded ? "Show Less" : "Show More"}
                            <ChevronRight className={`h-3.5 w-3.5 transition-transform ${overviewExpanded ? "rotate-90" : ""}`} />
                          </button>
                        )}
                        {isLocked && (
                          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/10 px-4 py-3">
                            <p className="flex items-center gap-2 text-xs text-gray-600">
                              <Lock className="h-3.5 w-3.5 shrink-0 text-primary" />
                              {(basicRemaining ?? 0) > 0
                                ? "Use a free unlock to reveal the full narrative and exact location of this deal."
                                : "Full narrative, financials and seller contact available to members."}
                            </p>
                            {renderUnlockCta("shrink-0 rounded-lg px-4 py-1.5 text-xs font-extrabold transition-opacity hover:opacity-90 disabled:opacity-60")}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* BL Score — Acquisition Intelligence */}
                  {blResult && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-gray-100">
                        <div className="flex items-center gap-3">
                          <div className="h-6 w-1 rounded-full" style={{ backgroundColor: GOLD }} />
                          <h2 className="section-title text-lg text-foreground">BL Score™ — Acquisition Intelligence</h2>
                        </div>
                        <span className="rounded-full bg-gray-100 px-2.5 py-1 text-[0.6rem] font-bold text-gray-500">{blResult.score} / 100</span>
                      </div>
                      <div className="flex flex-col sm:flex-row items-center gap-6 p-5">
                        <div className="flex shrink-0 flex-col items-center">
                          <ScoreDonut score={blResult.score} />
                          <span className="mt-1 text-[0.6rem] font-semibold uppercase tracking-widest" style={{ color: scoreColor(blResult.score) }}>
                            {blResult.score >= 75 ? "Strong" : blResult.score >= 50 ? "Solid" : "Developing"}
                          </span>
                        </div>
                        <div className="flex-1 w-full space-y-3">
                          {blResult.categories.map((c) => (
                            <ScoreBarRow key={c.label} label={c.label} score={c.score} note={c.note} />
                          ))}
                        </div>
                      </div>
                      <p className="px-5 pb-4 text-[0.62rem] text-gray-400">
                        Computed from disclosed listing data only. Informational — not investment advice.
                      </p>
                    </div>
                  )}

                  {/* Deal Highlights */}
                  {highlights.length > 0 && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
                        <div className="h-6 w-1 rounded-full" style={{ backgroundColor: GOLD }} />
                        <h2 className="section-title text-lg text-foreground">Deal Highlights</h2>
                      </div>
                      <div className="p-4 grid grid-cols-2 gap-3">
                        {highlights.map((h, i) => (
                          <div key={i} className="flex items-center gap-3 rounded-xl border border-gray-100 p-3 bg-gray-50">
                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg" style={{ background: `${h.accent}18` }}>
                              <h.Icon className="h-4 w-4" style={{ color: h.accent }} />
                            </div>
                            <div className="min-w-0">
                              <p className="text-[0.58rem] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">{h.label}</p>
                              <p className="text-sm font-extrabold leading-tight truncate"
                                style={{ color: [RED, GOLD, GREEN, TEAL].includes(h.accent) ? h.accent : INK }}>
                                {h.value}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Key Highlights */}
                  {canSeeContent && listing.additional_notes && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
                        <div className="h-6 w-1 rounded-full" style={{ backgroundColor: GOLD }} />
                        <h2 className="section-title text-lg text-foreground">Key Highlights</h2>
                      </div>
                      <div className="p-5 space-y-3">
                        {listing.additional_notes.split("\n").filter(Boolean).map((line, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div className="mt-2 h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: GOLD }} />
                            <p className="text-sm text-gray-700 leading-relaxed">{line}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Business Details */}
                  {canSeeContent && ((canSeeFullDetails && listing.website_url) || listing.established_year) && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
                        <div className="h-6 w-1 rounded-full" style={{ backgroundColor: TEAL }} />
                        <h2 className="section-title text-lg text-foreground">Business Details</h2>
                      </div>
                      <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
                        {canSeeFullDetails && listing.website_url && (
                          <div className="flex items-start gap-3">
                            <Globe className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                            <div className="min-w-0">
                              <p className="text-xs text-gray-400 mb-0.5">Website</p>
                              <a href={listing.website_url} target="_blank" rel="noreferrer"
                                className="text-primary font-medium hover:underline break-all">{listing.website_url}</a>
                            </div>
                          </div>
                        )}
                        {listing.established_year && (
                          <div className="flex items-start gap-3">
                            <Calendar className="h-4 w-4 text-gray-400 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-xs text-gray-400 mb-0.5">Established</p>
                              <p className="font-semibold text-gray-900">
                                {listing.established_month ? `${listing.established_month}/` : ""}{listing.established_year}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Agent Notes */}
                  {canSeeContent && listing.agent_notes && (
                    <div className="rounded-2xl overflow-hidden shadow-sm border border-white/10"
                      style={{ background: `linear-gradient(135deg, ${NAVY}, ${NAVY2})` }}>
                      <div className="flex items-center gap-3 px-5 py-3 border-b border-white/10">
                        <div className="h-6 w-1 rounded-full" style={{ backgroundColor: GOLD }} />
                        <h2 className="section-title text-lg" style={{ color: GOLD }}>Additional Notes</h2>
                      </div>
                      <div className="p-5 space-y-3">
                        {listing.agent_notes.split("\n").filter(Boolean).map((line, i) => (
                          <div key={i} className="flex items-start gap-3">
                            <div className="mt-2 h-2 w-2 rounded-full flex-shrink-0" style={{ backgroundColor: GOLD }} />
                            <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.82)" }}>{line}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Seller Contact — full-detail roles */}
                  {canSeeFullDetails && (listing.seller_name || listing.seller_email || listing.seller_phone) && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
                        <div className="h-6 w-1 rounded-full" style={{ backgroundColor: GOLD }} />
                        <h2 className="section-title text-lg text-foreground">Seller Contact</h2>
                      </div>
                      <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {[
                          { label: "Name",  value: listing.seller_name  },
                          { label: "Email", value: listing.seller_email },
                          { label: "Phone", value: formatPhone(listing.seller_phone) },
                        ].filter((r) => r.value).map((r) => (
                          // Theme tokens, not inline hex: a hardcoded light box stays
                          // light on the dark theme while gray-900 text remaps to
                          // cream — cream-on-white was unreadable.
                          <div key={r.label} className="rounded-xl border border-border/70 bg-muted/40 p-4">
                            <p className="mb-1 text-[0.6rem] font-bold uppercase tracking-widest text-muted-foreground">{r.label}</p>
                            <p className="text-sm font-semibold text-gray-900 break-all">{r.value}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Seller / Agent Account — admin */}
                  {[sellerInfo && { title: "Seller Account", info: sellerInfo }, agentInfo && { title: "Agent Account", info: agentInfo }]
                    .filter(Boolean).map((block) => {
                      const { title, info } = block as { title: string; info: UserType };
                      return (
                        <div key={title} className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                          <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
                            <div className="h-6 w-1 rounded-full" style={{ backgroundColor: GOLD }} />
                            <h2 className="section-title text-lg text-foreground">{title}</h2>
                          </div>
                          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
                            {[
                              { label: "Name",   value: `${info.first_name} ${info.last_name}` },
                              { label: "Email",  value: info.email },
                              { label: "Phone",  value: formatPhone(info.phone) },
                              { label: "Status", value: info.status },
                              { label: "Roles",  value: info.roles.join(", ") },
                              { label: "Joined", value: new Date(info.created_at).toLocaleDateString() },
                            ].filter((r) => r.value).map((r) => (
                              <div key={r.label}>
                                <p className="text-xs text-gray-400 mb-0.5">{r.label}</p>
                                <p className="font-semibold text-gray-900 capitalize">{r.value}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                </>
              )}

              {/* ═══ FINANCIALS TAB ═══ */}
              {currentTab === "financials" && (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[
                      listing.revenue      && { label: "Total Revenue (TTM)", value: fmt(listing.revenue), accent: INK },
                      listing.ebitda       && { label: "EBITDA",              value: fmt(listing.ebitda),  accent: GOLD },
                      marginPct != null    && { label: "EBITDA Margin",       value: `${marginPct.toFixed(1)}%`, accent: GREEN },
                      cfMultiple           && { label: "Cash-Flow Multiple",  value: cfMultiple,           accent: TEAL },
                    ].filter(Boolean).map((t: any) => (
                      <div key={t.label} className="rounded-2xl bg-white border border-gray-100 shadow-sm p-4">
                        <p className="text-[0.58rem] font-bold uppercase tracking-widest text-gray-400">{t.label}</p>
                        <p className="mt-1.5 text-2xl font-extrabold" style={{ color: t.accent }}>{t.value}</p>
                      </div>
                    ))}
                  </div>

                  {/* Income snapshot */}
                  <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                    <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
                      <div className="h-6 w-1 rounded-full" style={{ backgroundColor: GOLD }} />
                      <h2 className="section-title text-lg text-foreground">Income Snapshot (TTM)</h2>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {[
                        listing.revenue   != null && { label: "Total Revenue",        value: fmt(listing.revenue),   strong: true },
                        listing.ebitda    != null && { label: "EBITDA",               value: fmt(listing.ebitda),    strong: true },
                        listing.revenue != null && listing.ebitda != null && { label: "Operating Expenses (implied)", value: fmt(listing.revenue - listing.ebitda), strong: false },
                        marginPct != null && { label: "EBITDA Margin",         value: `${marginPct.toFixed(1)}%`, strong: false },
                      ].filter(Boolean).map((r: any) => (
                        <div key={r.label} className="flex items-center justify-between px-5 py-3">
                          <span className={`text-sm ${r.strong ? "font-bold text-gray-900" : "text-gray-500"}`}>{r.label}</span>
                          <span className={`text-sm ${r.strong ? "font-extrabold text-gray-900" : "font-semibold text-gray-600"}`}>{r.value}</span>
                        </div>
                      ))}
                    </div>
                    <p className="px-5 py-3 text-[0.62rem] text-gray-400 border-t border-gray-100">
                      Operating expenses are implied (Revenue − EBITDA) and exclude owner add-backs. Full financials available upon executed NDA.
                    </p>
                  </div>

                  {/* Debt-service estimate */}
                  {dscrEst && listing.asking_price && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
                        <div className="h-6 w-1 rounded-full" style={{ backgroundColor: TEAL }} />
                        <h2 className="section-title text-lg text-foreground">Debt-Service Estimate</h2>
                      </div>
                      <div className="p-5 grid grid-cols-2 sm:grid-cols-4 gap-4">
                        {[
                          { label: "Est. SBA Loan", value: fmt(dscrEst.loanAmount) },
                          { label: "Annual Debt Svc.", value: fmt(dscrEst.totalAnnualDebtService) },
                          { label: "Est. DSCR", value: `${dscrEst.dscr.toFixed(2)}×`, tone: dscrEst.dscr >= SBA_MIN_DSCR ? GREEN : RED },
                          // Full 7(a) check (loan cap, equity, DSCR) — same verdict as the SBA Calc tab.
                          { label: "SBA Eligible", value: dscrEst.status === "eligible" ? "Likely" : "Review", tone: dscrEst.status === "eligible" ? GREEN : GOLD },
                        ].map((r: any) => (
                          <div key={r.label}>
                            <p className="text-[0.58rem] font-bold uppercase tracking-widest text-gray-400">{r.label}</p>
                            <p className="mt-1 text-lg font-extrabold" style={{ color: r.tone ?? INK }}>{r.value}</p>
                          </div>
                        ))}
                      </div>
                      {dscrEst.status !== "eligible" && dscrEst.reasons.length > 0 && (
                        <p className="px-5 pb-2 text-xs text-muted-foreground">{dscrEst.reasons.join(" · ")}</p>
                      )}
                      <p className="px-5 pb-4 text-[0.62rem] text-gray-400">
                        Estimated using SBA 7(a) assumptions — {SBA_DEFAULTS.downPaymentPct}% buyer equity, {SBA_DEFAULTS.interestRate}% rate, {SBA_DEFAULTS.loanTermYears}-year term, {SBA_DEFAULTS.closingCostsPct}% closing costs and the SBA guaranty fee financed, EBITDA as cash flow. Open the SBA Calc tab to model your own terms.
                      </p>
                      <FundingCta listingId={listing.id} className="mx-5 mb-5" />
                    </div>
                  )}
                </>
              )}

              {/* ═══ SBA CALC TAB ═══ */}
              {currentTab === "sba" && (
                <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-4 sm:p-6">
                  {(listing.is_sba_approved || listing.seller_financing) && (
                    <div className="mb-5 flex flex-wrap gap-2">
                      {listing.is_sba_approved && (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-500">
                          <CheckCircle className="h-3.5 w-3.5" /> Seller reports SBA pre-approval
                        </span>
                      )}
                      {listing.seller_financing && (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                          <CheckCircle className="h-3.5 w-3.5" /> Seller financing offered — model it under Seller financing
                        </span>
                      )}
                    </div>
                  )}
                  <SbaCalculatorForm
                    initialValues={{
                      ...(listing.asking_price ? { askingPrice: Number(listing.asking_price) } : {}),
                      // Listings carry EBITDA (owner pay already out), not SDE.
                      ...(listing.ebitda ? { cashFlowBasis: "ebitda" as const, cashFlow: Number(listing.ebitda) } : {}),
                    }}
                  />
                  <FundingCta listingId={listing.id} className="mt-6" />
                </div>
              )}

              {/* ═══ VALUATION TAB ═══ */}
              {currentTab === "valuation" && (
                <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-4 sm:p-6">
                  <ValuationCalculatorForm
                    // Keyed by listing so moving to a related listing re-seeds the inputs.
                    key={listing.id}
                    initialIndustryId={listing.industry_category_id ?? null}
                    initialValues={{
                      ...(listing.revenue ? { revenue: Number(listing.revenue) } : {}),
                      ...(listing.asking_price ? { askingPrice: Number(listing.asking_price) } : {}),
                      // Listings carry EBITDA, not a P&L: enter it as the profit line
                      // with the add-backs zeroed, so adjusted EBITDA = the listing's
                      // EBITDA and SDE only grows once the buyer adds owner pay.
                      // Without EBITDA the profit starts at $0 rather than the
                      // calculator's sample numbers, which would read as this business's.
                      netProfit: listing.ebitda ? Number(listing.ebitda) : 0,
                      ownerComp: 0, depreciation: 0, interest: 0, oneTime: 0,
                      basis: listing.ebitda ? "ebitda" as const : "auto" as const,
                      inventory: 0,
                      equipment: 0,
                      workingCapital: 0,
                      debt: 0,
                    }}
                    note={
                      <>
                        Pre-filled from this listing: revenue, asking price and{" "}
                        {listing.ebitda ? "EBITDA (entered as net profit, add-backs at $0)" : "no EBITDA — enter the profit line"}.
                        {" "}Add the owner's pay to see SDE, and inventory / equipment from the CIM once you have it.
                      </>
                    }
                  />
                </div>
              )}

              {/* ═══ 5-YR MODEL TAB ═══ */}
              {currentTab === "model" && listing.revenue && listing.ebitda && (
                <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-4 sm:p-6">
                  <FinancialModelForm
                    // Keyed by listing so moving to a related listing re-seeds the inputs.
                    key={listing.id}
                    listing={{ id: listing.id, title: listing.title ?? null }}
                    initialValues={{
                      ...(listing.asking_price ? { purchasePrice: Number(listing.asking_price) } : {}),
                      revenue: Number(listing.revenue),
                      // Clamped to the form's ±100% bounds so an odd listing can't open in an error state.
                      ebitdaMarginPct: Math.max(-100, Math.min(100, Math.round((Number(listing.ebitda) / Number(listing.revenue)) * 1000) / 10)),
                      // No seller note unless the seller says they'll carry one.
                      ...(listing.seller_financing ? {} : { sellerNotePct: 0 }),
                    }}
                    note={
                      <>
                        Pre-filled from this listing: {listing.asking_price ? "asking price, " : ""}revenue and the EBITDA margin
                        ({((Number(listing.ebitda) / Number(listing.revenue)) * 100).toFixed(1)}%).
                        {listing.seller_financing ? " The seller offers financing, so a seller note is included." : ""}
                        {" "}Year 1 starts at the listing's trailing revenue — tick "Grow Year 1" if the business is already past it.
                        {" "}Growth, capex, taxes and the exit are assumptions — set them from the CIM.
                        {Number(listing.ebitda) < 0 ? " The listing reports negative EBITDA, so this models a turnaround — check the cash you'd have to add." : ""}
                      </>
                    }
                  />
                </div>
              )}

              {/* ═══ LOCATION TAB ═══ */}
              {/* Free (Basic) buyers: no map, no address — locked panel with an
                  Upgrade CTA; only the public region line is shown. */}
              {currentTab === "location" && isLocked && (
                <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                  <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
                    <div className="h-6 w-1 rounded-full" style={{ backgroundColor: GOLD }} />
                    <h2 className="section-title text-lg text-foreground">Precise Location</h2>
                  </div>
                  <div className="p-5">
                    <div className="relative overflow-hidden rounded-xl border border-gray-200">
                      <div
                        className="h-64 w-full"
                        style={{ background: "repeating-linear-gradient(45deg,#EEF2F7,#EEF2F7 12px,#E3E9F2 12px,#E3E9F2 24px)" }}
                      />
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-white/60 backdrop-blur-[2px]">
                        <div className="flex h-11 w-11 items-center justify-center rounded-full" style={{ backgroundColor: `${GOLD}2A` }}>
                          <Lock className="h-5 w-5" style={{ color: GOLD }} />
                        </div>
                        <p className="text-sm font-bold text-gray-800">Exact address hidden</p>
                        {renderUnlockCta("rounded-xl px-5 py-2 text-xs font-extrabold shadow-md transition-opacity hover:opacity-90 disabled:opacity-60")}
                      </div>
                    </div>
                    {locationLabel && (
                      <p className="mt-4 flex items-center gap-2 text-sm text-gray-600">
                        <MapPin className="h-4 w-4 text-gray-400 shrink-0" />
                        <span><span className="font-semibold text-gray-400">Region:</span> {locationLabel}</span>
                      </p>
                    )}
                  </div>
                </div>
              )}
              {currentTab === "location" && !isLocked && (
                /* Details first, map second: the region/address/industry facts are
                   what buyers scan for, and the 420px map pushed them below the fold. */
                <div className="space-y-5">
                  <div className="rounded-2xl bg-white shadow-sm border border-gray-100 overflow-hidden">
                    <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
                      <div className="h-6 w-1 rounded-full" style={{ backgroundColor: GOLD }} />
                      <h2 className="section-title text-lg text-foreground">Location Details</h2>
                      {!canSeeFullDetails && (
                        <span className="ml-auto rounded-full bg-amber-500/15 px-2.5 py-1 text-[0.58rem] font-bold uppercase tracking-wide text-amber-500">
                          Approx · exact address post-NDA
                        </span>
                      )}
                    </div>
                    <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
                      {[
                        { label: "Region", value: locationLabel },
                        canSeeFullDetails && { label: "Address", value: listing.address_line },
                        canSeeFullDetails && { label: "ZIP", value: listing.zip_code },
                        { label: "Industry", value: categoryName },
                      ].filter((r): r is { label: string; value: string } => Boolean(r && r.value)).map((r) => (
                        <div key={r.label} className="flex items-start gap-3">
                          <MapPin className="h-4 w-4 mt-0.5 shrink-0" style={{ color: GOLD }} />
                          <div className="min-w-0">
                            <p className="text-xs text-gray-400 mb-0.5">{r.label}</p>
                            <p className="font-semibold text-gray-900 break-words">{r.value}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl overflow-hidden border border-gray-200 shadow-sm">
                    <iframe
                      title="listing-map"
                      width="100%"
                      height="420"
                      style={{ border: 0, display: "block" }}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      src={`https://maps.google.com/maps?q=${mapQuery}&output=embed&z=12`}
                    />
                  </div>
                </div>
              )}

              {/* ═══ DEAL ACTIONS TAB ═══ */}
              {currentTab === "actions" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Members-only actions — free buyers upgrade to unlock */}
                  {isFreeBuyer && (
                    <div className="rounded-2xl border border-primary/30 bg-primary/10 p-5 sm:col-span-2">
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                        <Lock className="h-4 w-4 text-primary" />
                        Members-only deal actions
                      </div>
                      <p className="mt-1 text-xs text-gray-600 leading-relaxed">
                        {isLocked
                          ? "Unlock this deal to message the advisor and make an offer. Saving favorites, deal locks and the SBA calculator are reserved for members."
                          : "You've unlocked this deal, so you can message the advisor and make an offer. Saving favorites, deal locks and the SBA calculator are reserved for members."}
                      </p>
                      <button
                        onClick={() => navigate("/choose-plan")}
                        className="mt-3 rounded-xl px-5 py-2.5 text-xs font-extrabold shadow-md transition-opacity hover:opacity-90"
                        style={{ backgroundColor: GOLD, color: NAVY }}
                      >
                        Upgrade to unlock →
                      </button>
                    </div>
                  )}
                  {/* Show interest */}
                  {canExpressInterest && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                        <Calendar className="h-4 w-4" style={{ color: GOLD }} />
                        Express Interest
                      </div>
                      <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                        Signal the advisor you'd like to learn more and open a private conversation.
                      </p>
                      {interestSuccess || alreadyInterested ? (
                        <div className="mt-3 flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-xs font-medium text-emerald-700">
                          <CheckCircle className="h-4 w-4 shrink-0" />
                          {interestSuccess ? "Interest submitted!" : "Interest already submitted."}
                        </div>
                      ) : showInterestForm ? (
                        <div className="mt-3 space-y-2">
                          <Textarea rows={3} placeholder="e.g. your budget, timeline, questions…"
                            value={interestMessage} onChange={(e) => setInterestMessage(e.target.value)}
                            className="resize-none text-sm" />
                          <div className="flex gap-2">
                            <Button onClick={handleShowInterest} disabled={interestLoading || !interestMessage.trim()} size="sm"
                              className="flex-1 rounded-xl text-white hover:opacity-95"
                              style={{ background: `linear-gradient(135deg, ${NAVY2}, ${NAVY})` }}>
                              {interestLoading ? "Sending…" : "Submit"}
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setShowInterestForm(false)} className="rounded-xl">Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <Button size="sm" className="mt-3 w-full rounded-xl text-white hover:opacity-95"
                          style={{ background: `linear-gradient(135deg, ${NAVY2}, ${NAVY})` }}
                          onClick={() => user ? setShowInterestForm(true) : navigate("/sign-in")}>
                          <Heart className="h-4 w-4" /> Show Interest
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Chat */}
                  {isBuyer && alreadyInterested && interestId && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                        <MessageCircle className="h-4 w-4" style={{ color: GOLD }} />
                        Message Advisor
                      </div>
                      <p className="mt-1 text-xs text-gray-500 leading-relaxed">Continue your private conversation about this deal.</p>
                      <Button variant="outline" size="sm" className="mt-3 w-full rounded-xl"
                        onClick={() => window.dispatchEvent(new Event("open-interest-chat"))}>
                        <MessageCircle className="h-3.5 w-3.5" /> Open Chat
                      </Button>
                    </div>
                  )}

                  {/* SBA calc — member buyers only */}
                  {isBuyer && !isFreeBuyer && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                        <Calculator className="h-4 w-4" style={{ color: GOLD }} />
                        SBA Financing
                      </div>
                      <p className="mt-1 text-xs text-gray-500 leading-relaxed">Model this deal with the SBA 7(a) calculator, pre-filled from the listing.</p>
                      <Button variant="outline" size="sm" className="mt-3 w-full rounded-xl" onClick={() => setActiveTab("sba")}>
                        <Calculator className="h-3.5 w-3.5" /> Open Calculator
                      </Button>
                    </div>
                  )}

                  {/* Favorite — free buyers see the members-only banner instead */}
                  {canFavorite && !isFreeBuyer && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                        <Heart className="h-4 w-4" style={{ color: RED }} />
                        {isFavorited ? "Saved" : "Save Deal"}
                      </div>
                      <p className="mt-1 text-xs text-gray-500 leading-relaxed">Keep this opportunity in your favorites for quick access.</p>
                      <Button variant="outline" size="sm" className="mt-3 w-full rounded-xl" onClick={handleToggleFavorite} disabled={favLoading}>
                        <Heart className={`h-3.5 w-3.5 ${isFavorited ? "fill-rose-500 text-rose-500" : ""}`} />
                        {isFavorited ? "Remove from Favorites" : "Add to Favorites"}
                      </Button>
                    </div>
                  )}

                  {/* Export PDF */}
                  <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                      <Download className="h-4 w-4" style={{ color: GOLD }} />
                      Deal Summary PDF
                    </div>
                    <p className="mt-1 text-xs text-gray-500 leading-relaxed">Download a shareable one-page summary of this listing.</p>
                    <Button variant="outline" size="sm" className="mt-3 w-full rounded-xl" onClick={handleExportPDF} disabled={pdfLoading}>
                      <Download className="h-3.5 w-3.5" /> {pdfLoading ? "Generating…" : "Export PDF"}
                    </Button>
                  </div>

                  {/* Listing lock */}
                  {canUseListingLock && tierAllowsLock && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                        <Lock className="h-4 w-4" style={{ color: GOLD }} />
                        Listing Lock
                      </div>
                      <p className="mt-1 text-xs text-gray-500 leading-relaxed">
                        Reserve this listing so other buyers can't access it during your review window.
                      </p>
                      {lockUntil && (
                        <p className="mt-2 text-xs font-medium text-amber-600">Locked until {new Date(lockUntil).toLocaleString()}</p>
                      )}
                      {lockedByMe ? (
                        <Button variant="outline" size="sm" className="mt-3 w-full rounded-xl border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                          onClick={handleReleaseLock} disabled={lockLoading}>
                          {lockLoading ? "Releasing…" : "Release Lock"}
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" className="mt-3 w-full rounded-xl" onClick={handleLockListing} disabled={lockLoading}>
                          <Lock className="h-3.5 w-3.5" /> {lockLoading ? "Locking…" : "Lock This Listing"}
                        </Button>
                      )}
                    </div>
                  )}

                  {/* Edit */}
                  {canEdit && (
                    <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-5">
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-900">
                        <Pencil className="h-4 w-4" style={{ color: GOLD }} />
                        Manage Listing
                      </div>
                      <p className="mt-1 text-xs text-gray-500 leading-relaxed">Edit the listing details, financials, and status.</p>
                      <Link to={`/listings/${listing.id}/edit`} className="block">
                        <Button variant="outline" size="sm" className="mt-3 w-full rounded-xl">
                          <Pencil className="h-3.5 w-3.5" /> Edit Listing
                        </Button>
                      </Link>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Sidebar ─────────────────────────────────────────────── */}
            {showSidebar && (
              <aside className="space-y-4 lg:sticky lg:top-[12rem]">
                {/* Listing Lock — first card in the rail */}
                {canUseListingLock && tierAllowsLock && (
                  <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-4">
                    <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-gray-800">
                      <Lock className="h-3.5 w-3.5" /> Listing Lock
                    </div>
                    <p className="mb-2 text-xs text-gray-500 leading-relaxed">
                      Lock this listing so other buyers can't access it during your review window.
                    </p>
                    {lockUntil && (
                      <p className="mb-2 text-xs font-medium text-amber-600">Locked until {new Date(lockUntil).toLocaleString()}</p>
                    )}
                    {lockedByMe ? (
                      <Button variant="outline" size="sm" className="w-full rounded-xl border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                        onClick={handleReleaseLock} disabled={lockLoading}>
                        {lockLoading ? "Releasing…" : "Release Lock"}
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="w-full rounded-xl" onClick={handleLockListing} disabled={lockLoading}>
                        <Lock className="h-3.5 w-3.5" /> {lockLoading ? "Locking…" : "Lock This Listing"}
                      </Button>
                    )}
                  </div>
                )}

                {/* BL Advisor card — anonymous visitors and Basic buyers who haven't
                    unlocked THIS deal get the members-only lock instead, whether or
                    not an advisor is assigned (the API redacts the advisor for them). */}
                {canSeeAdvisorCard && (isLocked || !user) && (
                  <MembersOnlyContactCard
                    agentName={listing.agent_name}
                    onUpgrade={() => navigate(user ? "/choose-plan" : "/sign-in")}
                  />
                )}
                {listing.agent_name && canSeeAdvisorCard && !isLocked && user && (
                  <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-4 space-y-4">
                    {/* Header — LISTING ADVISOR eyebrow + name (matches Figma) */}
                    <div className="flex items-center gap-3">
                      {listing.agent_avatar_url ? (
                        <img src={assetUrl(listing.agent_avatar_url)!} alt={listing.agent_name}
                          className="h-12 w-12 rounded-2xl object-cover ring-1 shrink-0"
                          style={{ ['--tw-ring-color' as string]: `${GOLD}55` }} />
                      ) : (
                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-sm font-extrabold"
                          style={{ background: `linear-gradient(135deg, ${GOLD}22, ${GOLD}44)`, color: GOLD, border: `1px solid ${GOLD}55` }}>
                          {listing.agent_name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-[0.6rem] font-bold uppercase tracking-widest" style={{ color: "var(--color-muted-foreground)" }}>Listing Advisor</p>
                        <p className="text-sm font-extrabold leading-tight truncate" style={{ color: INK }}>{listing.agent_name}</p>
                        {listing.agent_verified && (
                          <span className="inline-flex items-center gap-1 mt-0.5 text-[0.6rem] font-semibold text-emerald-600">
                            <CheckCircle className="h-3 w-3" /> Verified
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Usage meters — messages/day + deals unlocked/month */}
                    {usage && (
                      <div className="rounded-xl p-3 space-y-3" style={{ backgroundColor: "var(--color-muted)", border: "1px solid var(--color-border)" }}>
                        <UsageMeter label="Messages today" used={usage.messages_used} limit={usage.messages_limit} />
                        <UsageMeter label="Deals unlocked this month" used={usage.deals_used} limit={usage.deals_limit} />
                      </div>
                    )}

                    {/* Express-interest form (inline) OR the primary action buttons */}
                    {canExpressInterest && showInterestForm && !alreadyInterested && !interestSuccess ? (
                      <div className="space-y-2">
                        <p className="text-[0.6rem] font-bold uppercase tracking-widest" style={{ color: "var(--color-muted-foreground)" }}>Your Message</p>
                        <Textarea rows={3} placeholder="e.g. your offer, preferred visit time, questions…"
                          value={interestMessage} onChange={(e) => setInterestMessage(e.target.value)}
                          className="resize-none text-sm" />
                        <div className="flex gap-2">
                          <Button onClick={handleShowInterest} disabled={interestLoading || !interestMessage.trim()} size="sm"
                            className="flex-1 rounded-xl bg-gold-gradient text-primary-foreground hover:brightness-[1.06]">
                            {interestLoading ? "Sending…" : "Send to advisor"}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setShowInterestForm(false)} className="rounded-xl">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(alreadyInterested || canExpressInterest) && (
                          <button
                            onClick={handleMessageAdvisor}
                            disabled={outOfMessages && !alreadyInterested}
                            className="flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-extrabold bg-gold-gradient text-primary-foreground shadow-md transition-[filter] hover:brightness-[1.06] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100">
                            <MessageCircle className="h-4 w-4" /> Message advisor
                          </button>
                        )}

                        {canExpressInterest && (
                          interestSuccess || alreadyInterested ? (
                            <div className="flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold"
                              style={{ backgroundColor: `${GREEN}1A`, color: GREEN, border: `1px solid ${GREEN}40` }}>
                              <CheckCircle className="h-4 w-4 shrink-0" />
                              {interestSuccess ? "Offer sent to your advisor" : "Interest submitted"}
                            </div>
                          ) : (
                            <button
                              onClick={() => (user ? setShowInterestForm(true) : navigate("/sign-in"))}
                              disabled={outOfMessages}
                              className="flex w-full items-center justify-center gap-2 rounded-xl border-2 py-2.5 text-sm font-bold transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                              style={{ borderColor: GOLD, color: GOLD }}>
                              <DollarSign className="h-4 w-4" /> Make an offer
                            </button>
                          )
                        )}

                        {/* "x of 5 messages left today" — only when the tier is capped */}
                        {usage?.messages_limit != null && (
                          <p className="pt-0.5 text-center text-[0.7rem] font-medium"
                            style={{ color: outOfMessages ? RED : "var(--color-muted-foreground)" }}>
                            {outOfMessages
                              ? "Daily message limit reached — resets tomorrow"
                              : `${usage.messages_remaining} of ${usage.messages_limit} messages left today`}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Direct contact — staff only. Buyers reach the advisor through
                        "Message advisor" / "Make an offer" above, so the conversation
                        is recorded against the deal; the API no longer sends them an
                        address to mail anyway (see _ADVISOR_CONTACT_FIELDS server-side).
                        Rendered off the fields themselves rather than falling back to
                        href="#", which showed every buyer two dead buttons. */}
                    {(listing.agent_email || listing.agent_phone) && (
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        {listing.agent_email && (
                          <a href={`mailto:${listing.agent_email}`}
                            className="flex items-center justify-center gap-1 rounded-xl border-2 py-2 text-xs font-semibold transition-colors hover:bg-gray-50"
                            style={{ borderColor: GOLD, color: GOLD }}>
                            <Mail className="h-3.5 w-3.5" /> Mail
                          </a>
                        )}
                        {listing.agent_phone && (
                          <a href={`tel:${listing.agent_phone}`}
                            className="flex items-center justify-center gap-1 rounded-xl border-2 py-2 text-xs font-semibold transition-colors hover:bg-gray-50"
                            style={{ borderColor: GOLD, color: GOLD }}>
                            <Phone className="h-3.5 w-3.5" /> Call
                          </a>
                        )}
                      </div>
                    )}

                    <button
                      onClick={() => setShowAdvisorModal(true)}
                      className="w-full text-center text-xs font-semibold transition-colors hover:underline"
                      style={{ color: GOLD }}>
                      View Profile →
                    </button>
                  </div>
                )}

                {/* SBA calc — member buyers only (free buyers have it locked) */}
                {isBuyer && !isFreeBuyer && (
                  <button
                    onClick={() => setActiveTab("sba")}
                    className="flex w-full items-center justify-center gap-2 rounded-2xl border-2 bg-white py-3 text-sm font-bold shadow-sm transition-colors hover:bg-gray-50"
                    style={{ borderColor: GOLD, color: GOLD }}>
                    <Calculator className="h-4 w-4" /> Calculate SBA
                  </button>
                )}

                {/* Show Interest — no agent (anonymous visitors see the members-only
                    card above instead, so this is for signed-in member buyers) */}
                {!listing.agent_name && canExpressInterest && user && (
                  <div className="rounded-2xl bg-white shadow-sm border border-gray-100 p-4 space-y-3">
                    {interestSuccess || alreadyInterested ? (
                      <div className="flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-3 py-2.5 text-xs font-medium text-emerald-700">
                        <Heart className="h-4 w-4 fill-current shrink-0" />
                        {interestSuccess ? "Interest submitted! The seller will be notified." : "You've already expressed interest."}
                      </div>
                    ) : showInterestForm ? (
                      <div className="space-y-2">
                        <p className="text-[0.6rem] font-bold text-gray-400 uppercase tracking-widest">Your Message</p>
                        <Textarea rows={3} placeholder="e.g. your budget, timeline, questions…"
                          value={interestMessage} onChange={(e) => setInterestMessage(e.target.value)}
                          className="resize-none text-sm" />
                        <div className="flex gap-2">
                          <Button onClick={handleShowInterest} disabled={interestLoading || !interestMessage.trim()} size="sm"
                            className="flex-1 rounded-xl text-white hover:opacity-95"
                            style={{ background: `linear-gradient(135deg, ${NAVY2}, ${NAVY})` }}>
                            <Heart className="h-3.5 w-3.5" />
                            {interestLoading ? "Submitting…" : "Submit Interest"}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setShowInterestForm(false)} className="rounded-xl">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <Button size="sm" className="w-full rounded-xl text-sm font-semibold text-white hover:opacity-95 shadow-md"
                        style={{ background: `linear-gradient(135deg, ${NAVY2}, ${NAVY})` }}
                        onClick={() => user ? setShowInterestForm(true) : navigate("/sign-in")}>
                        <Heart className="h-4 w-4" /> Show Interest
                      </Button>
                    )}
                  </div>
                )}

              </aside>
            )}
          </div>
          );
        })()}
      </div>{/* ── end full-width content wrapper ────────────────────────── */}

      {/* ── Explore More Opportunities ─────────────────────────────────── */}
      {moreListings.length > 0 && (
        <div className="border-t border-gray-200 bg-white py-10 mt-4">
          <div className="w-full px-4 sm:px-6 lg:px-8">
            <div className="mb-6 flex items-center justify-between gap-3">
              <h2 className="text-xl font-extrabold text-gray-900 sm:text-2xl">Explore More Opportunities</h2>
              <div className="flex items-center gap-2">
                <button onClick={() => scrollCarousel("left")}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 transition-colors">
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button onClick={() => scrollCarousel("right")}
                  className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50 transition-colors">
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div ref={carouselRef} className="flex gap-4 overflow-x-auto pb-3" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
              {moreListings.map((l) => {
                const lImg  = l.listing_image_url ? assetUrl(l.listing_image_url) : null;
                const lCat  = moreCategories.find((c) => c.id === l.industry_category_id)?.name ?? "Business";
                const lLoc  = [l.city_name, l.state_name, l.country_name].filter(Boolean).join(", ");
                return (
                  <Link key={l.id} to={`/listings/${l.id}`} state={{ from: backFrom }}
                    className="group flex flex-col shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md"
                    style={{ width: 280 }}>
                    <div className="relative h-44 w-full overflow-hidden bg-gradient-to-br from-slate-100 to-slate-200">
                      {lImg ? (
                        <img src={lImg} alt={l.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center">
                          <Building2 className="h-10 w-10 text-slate-300" />
                        </div>
                      )}
                      <div className="absolute left-3 top-3">
                        <span className="inline-flex items-center rounded-full border border-primary/20 bg-white/90 px-2.5 py-1 text-[0.6rem] font-semibold uppercase tracking-[0.1em] text-primary backdrop-blur-sm">
                          {lCat}
                        </span>
                      </div>
                      <div className="absolute right-3 top-3">
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide text-emerald-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-1 flex-col gap-2 p-4">
                      <h3 className="line-clamp-2 text-sm font-extrabold leading-snug text-gray-900 group-hover:text-primary transition-colors">{l.title}</h3>
                      {(l.display_overview || l.overview) && (
                        <p className="line-clamp-2 text-xs leading-5 text-gray-500">{l.display_overview || l.overview}</p>
                      )}
                      <div className="mt-auto flex items-center justify-between pt-2">
                        {l.asking_price ? (
                          <span className="text-sm font-extrabold" style={{ color: INK }}>{fmt(l.asking_price)}</span>
                        ) : <span />}
                        {lLoc && (
                          <span className="flex items-center gap-1 text-[0.65rem] text-gray-400">
                            <MapPin className="h-3 w-3" /> {lLoc}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>

    {/* Floating chat widget */}
    {isBuyer && alreadyInterested && interestId && (
      <InterestChat interestId={interestId} listingTitle={listing?.title} />
    )}

    {showAdvisorModal && listing && (
      <AdvisorProfileModal listing={listing} onClose={() => setShowAdvisorModal(false)} />
    )}

    {/* ── Photo lightbox ─────────────────────────────────────────────── */}
    {showPhotoModal && categoryImageUrl && (
      <div
        className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-8"
        style={{ backgroundColor: "rgba(0,0,0,0.85)" }}
        onClick={() => setShowPhotoModal(false)}
        role="dialog"
        aria-modal="true"
        aria-label="Listing photo"
      >
        <button
          onClick={() => setShowPhotoModal(false)}
          className="absolute top-4 right-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
        <figure className="flex max-h-full max-w-full flex-col items-center gap-3" onClick={(e) => e.stopPropagation()}>
          <img
            src={categoryImageUrl}
            alt={displayTitle}
            className="max-h-[85dvh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
          />
          <figcaption className="text-center text-sm font-medium text-white/80">{displayTitle}</figcaption>
        </figure>
      </div>
    )}
    </>
  );
}


